import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CirclePlus, Pencil } from "lucide-react";
import { DeleteBtn } from "@/components/Delete";
import { LoaderSpin } from "@/components/Loaders";
import { useToastNavigate } from "@/utils/toastNavigate";
import {
  getEmployeeSalaryHistories,
  createEmployeeSalaryHistory,
  updateEmployeeSalaryHistory,
  deleteEmployeeSalaryHistory,
} from "@/Api/employeeSalaryHistories";

const schema = z.object({
  salary: z.string({ message: "*requerido" }).min(1, "*requerido"),
  effective_date: z.string({ message: "*requerido" }).min(1, "*requerido"),
  notes: z.string().optional(),
});

type SchemaType = z.infer<typeof schema>;

const formatDate = (value?: string | null) =>
  value ? value.slice(0, 10).split("-").reverse().join("/") : "-";

const formatCurrency = (value: string | number) =>
  Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const HistoryForm = ({
  employeeId,
  editing,
  onSaved,
}: {
  employeeId: number;
  editing?: any;
  onSaved: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState("default");
  const { toast } = useToastNavigate();

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      salary: editing?.salary ? String(editing.salary) : "",
      effective_date: editing?.effective_date?.slice(0, 10) || "",
      notes: editing?.notes || "",
    },
  });

  const onSubmit = async (data: SchemaType) => {
    setState("loading");
    const payload = editing ? data : { ...data, employee_id: employeeId };

    const { data: apiResponse, ok } = editing
      ? await updateEmployeeSalaryHistory(editing.uuid, payload)
      : await createEmployeeSalaryHistory(payload);

    if (!ok) {
      setState("error");
      toast({
        description: apiResponse?.message || "Erro ao salvar",
        variant: "destructive",
      });
      setTimeout(() => setState("default"), 2000);
      return;
    }

    setState("default");
    setIsOpen(false);
    form.reset();
    toast({ description: "Salvo com sucesso!" });
    onSaved();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {editing ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          >
            <Pencil className="size-5" />
          </Button>
        ) : (
          <Button type="button" size="sm" variant="outline" className="gap-1">
            <CirclePlus className="size-4" />
            Novo Salário
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogDescription className="text-lg">
            {editing ? "Editar Salário" : "Novo Registro de Salário"}
          </DialogDescription>
        </DialogHeader>
        <Form onSubmit={form.handleSubmit(onSubmit)} {...form} className="space-y-3">
          <Form.Row>
            <Form.Field name="salary">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Valor <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} type="number" step="0.01" placeholder="0.00" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
            <Form.Field name="effective_date">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Data <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} type="date" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>
          <Form.Row>
            <Form.Field name="notes">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>Observações</Form.Label>
                  <Form.Control>
                    <Textarea {...field} placeholder="Opcional" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>
          <DialogFooter>
            <Button type="submit" className="w-full" disabled={state !== "default"}>
              {state === "loading" ? <LoaderSpin /> : "Salvar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export const SalaryHistorySection = ({ employeeId }: { employeeId: number }) => {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    const { data } = await getEmployeeSalaryHistories(employeeId);
    setHistory(data?.response || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Histórico de Salários</label>
        <HistoryForm employeeId={employeeId} onSaved={fetchData} />
      </div>
      {!loading && (
        <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
          <TableHeader>
            <TableRow>
              <TableHead>Salário</TableHead>
              <TableHead>Data</TableHead>
              <TableHead className="text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.map((item) => (
              <TableRow key={item.uuid}>
                <TableCell>{formatCurrency(item.salary)}</TableCell>
                <TableCell>{formatDate(item.effective_date)}</TableCell>
                <TableCell className="flex justify-center gap-1">
                  <HistoryForm employeeId={employeeId} editing={item} onSaved={fetchData} />
                  <DeleteBtn
                    params={{ func: deleteEmployeeSalaryHistory, funcParams: item.uuid }}
                    updateFront={fetchData}
                    referenceLabel={formatCurrency(item.salary)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {history.length < 1 && (
              <TableRow>
                <TableCell colSpan={3}>Nada foi encontrado.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
